import React from 'react';
import './Avatar.css';

interface AvatarProps {
  src?: string;
  alt: string;
  size?: 'small' | 'medium' | 'large';
  fallbackText?: string;
  isOnline?: boolean;
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size = 'medium',
  fallbackText,
  isOnline,
  className = ''
}) => {
  // Generate fallback text from alt if not provided
  const getFallbackText = () => {
    if (fallbackText) return fallbackText;
    
    // Extract initials from alt text
    const words = alt.split(' ').filter(word => word.length > 0);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    } else if (words.length === 1) {
      return words[0][0].toUpperCase();
    }
    return '?';
  };

  // Generate a consistent color based on the alt text
  const getBackgroundColor = () => {
    let hash = 0;
    for (let i = 0; i < alt.length; i++) {
      hash = alt.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate a pleasant color from the hash
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 55%)`;
  };

  const avatarClasses = [
    'avatar',
    `avatar--${size}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={avatarClasses} role="img" aria-label={alt}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className="avatar__image"
          onError={(e) => {
            // Hide image on error and show fallback
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
      
      {/* Fallback content - shown when no image or image fails to load */}
      <div 
        className="avatar__fallback"
        style={{ 
          backgroundColor: getBackgroundColor(),
          display: src ? 'none' : 'flex' 
        }}
      >
        {getFallbackText()}
      </div>
      
      {/* Online status indicator */}
      {isOnline !== undefined && (
        <div 
          className={`avatar__status ${isOnline ? 'avatar__status--online' : 'avatar__status--offline'}`}
          aria-label={isOnline ? 'Online' : 'Offline'}
        />
      )}
    </div>
  );
};

export default Avatar;